<template>
    <ModalBase
        :name="props.name"
        title="Descargar arqueo de caja"
        subtitle="Se abre el informe en una pestaña nueva."
        :width="720"
        @opened="sincronizarConFiltros"
        @close="$emit('close')"
    >
        <div class="tw:flex tw:flex-col tw:gap-4">
            <div class="tw:grid tw:grid-cols-1 tw:gap-4 tw:sm:grid-cols-2">
                <div class="tw:flex tw:flex-col tw:gap-1">
                    <label :class="labelClass" for="arqueo-del">Del</label>
                    <BaseDateInput id="arqueo-del" :value="fecha_inicio" data-cy="arqueo-del" @input="fecha_inicio = $event" />
                </div>
                <div class="tw:flex tw:flex-col tw:gap-1">
                    <label :class="labelClass" for="arqueo-al">Al</label>
                    <BaseDateInput id="arqueo-al" :value="fecha_fin" data-cy="arqueo-al" @input="fecha_fin = $event" />
                </div>
            </div>

            <div class="tw:grid tw:grid-cols-1 tw:gap-4 tw:sm:grid-cols-3">
                <div class="tw:flex tw:flex-col tw:gap-1">
                    <label :class="labelClass" for="arqueo-teatro">Teatro</label>
                    <BaseMultiselect
                        id="arqueo-teatro"
                        name="arqueo-teatro"
                        :value="teatroId"
                        :options="props.teatros"
                        label-key="nombre"
                        placeholder="Todos los recintos"
                        data-cy="arqueo-teatro"
                        @input="teatroId = $event || ''"
                    />
                </div>

                <div class="tw:flex tw:flex-col tw:gap-1">
                    <label :class="labelClass" for="arqueo-taquillero">Taquillero</label>
                    <BaseMultiselect
                        id="arqueo-taquillero"
                        name="arqueo-taquillero"
                        :value="taquilleroId"
                        :options="props.taquilleros"
                        label-key="nombre"
                        placeholder="Todos los agentes"
                        data-cy="arqueo-taquillero"
                        @input="taquilleroId = $event || ''"
                    />
                </div>

                <div class="tw:flex tw:flex-col tw:gap-1">
                    <label :class="labelClass" for="arqueo-compania">Compañía</label>
                    <BaseMultiselect
                        id="arqueo-compania"
                        name="arqueo-compania"
                        :value="companiaId"
                        :options="props.companias"
                        label-key="nombre"
                        placeholder="Todas las compañías"
                        data-cy="arqueo-compania"
                        @input="companiaId = $event || ''"
                    />
                </div>
            </div>
        </div>

        <template #footer>
            <div class="tw:flex tw:items-center tw:justify-end tw:gap-3">
                <Button variant="ghost" @click="$emit('close')">Cerrar</Button>
                <Button
                    icon="fas fa-cash-register"
                    data-cy="arqueo-enviar"
                    @click="descargar"
                >
                    Descargar
                </Button>
            </div>
        </template>
    </ModalBase>
</template>

<script setup>
    import { ref } from 'vue';
    import store from '@/store';
    import { config as appConfig } from '@/config.js';

    import ModalBase from '@/modules/admin/ui/ModalBase.vue';
    import Button from '@/modules/admin/ui/Button.vue';
    import BaseDateInput from '@/modules/admin/ui/BaseDateInput.vue';
    import BaseMultiselect from '@/modules/admin/ui/BaseMultiselect.vue';

    const props = defineProps({
        name: {
            type: String,
            required: true
        },
        /** Foto de los filtros vigentes en la pestaña. */
        filtros: {
            type: Object,
            default: () => ({})
        },
        teatros: {
            type: Array,
            default: () => []
        },
        taquilleros: {
            type: Array,
            default: () => []
        },
        companias: {
            type: Array,
            default: () => []
        }
    });

    const emit = defineEmits(['close']);

    const fecha_inicio = ref('');
    const fecha_fin = ref('');
    const teatroId = ref('');
    const taquilleroId = ref('');
    const companiaId = ref('');

    // Misma clase que los labels de `ResumenVentasFilters`: el template la usa
    // y sin declararla Vue avisa en cada render ("labelClass is not defined").
    const labelClass = 'tw:block tw:text-xs tw:font-semibold tw:text-slate-500 tw:uppercase tw:tracking-wide';


    function sincronizarConFiltros() {
        fecha_inicio.value = props.filtros.del || props.filtros.fecha_inicio || '';
        fecha_fin.value = props.filtros.al || props.filtros.fecha_fin || '';
        teatroId.value = props.filtros.teatro_id !== undefined ? props.filtros.teatro_id : '';
        taquilleroId.value = props.filtros.taquillero_id !== undefined ? props.filtros.taquillero_id : '';
        companiaId.value = props.filtros.teatro_company_id !== undefined ? props.filtros.teatro_company_id : '';
    }

    /**
     * El arqueo de caja es un HTML que el navegador descarga por GET, así que
     * se abre por URL (con `api_key`, igual que el legacy), no por axios.
     *
     * A diferencia del legacy, lo que el usuario elige en el modal MANDA: antes
     * la prop `teatro_id` pisaba al teatro seleccionado y el recinto elegido en
     * el desplegable no tenía efecto.
     */
    function descargar() {
        const parametros = {
            api_key: localStorage.getItem('token'),
            tid: (store.getters.getConfig || {}).hash_teatro,
            search_teatro_id: teatroId.value || props.filtros.teatro_id || ''
        };

        if (quien(taquilleroId.value)) parametros.taquillero_id = taquilleroId.value;
        if (quien(companiaId.value)) parametros.teatro_company_id = companiaId.value;
        if (fecha_inicio.value) parametros.fecha_inicio = fecha_inicio.value;
        if (fecha_fin.value) parametros.fecha_fin = fecha_fin.value;
        if (props.filtros.pasado) parametros.pasado = props.filtros.pasado;
        if (props.filtros.actual) parametros.actual = props.filtros.actual;

        const query = Object.keys(parametros)
            .filter(clave => parametros[clave] !== '' && parametros[clave] !== null && parametros[clave] !== undefined)
            .map(clave => `${encodeURIComponent(clave)}=${encodeURIComponent(parametros[clave])}`)
            .join('&');

        window.open(`${appConfig.API_BASE}/eventos/resumen-ventas/arqueo-caja?${query}`);

        emit('close');
    }

    /** Un id vacío es "sin filtrar": no viaja como parámetro. */
    function quien(valor) {
        return valor !== '' && valor !== null && valor !== undefined;
    }
</script>
